'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { cn } from '@ais-ui/utils';
import { LoadingSpinner } from './LoadingSpinner';
import { Button } from './Button';
import { ScrollArea } from './ScrollArea';
import { useToast } from './Toast/useToast';
import { ToasterInDialog } from './Toast/Toaster';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg md:w-full',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

interface DialogFormProps extends React.HTMLAttributes<HTMLDivElement> {
  showDialog: boolean;
  onOpenChange: (show: boolean) => void;
  title: string;
  description?: string;
  isLoading?: boolean;
  hasLoaded?: boolean;
  children?: React.ReactNode;
  buttonId: string;
  buttonText: string;
  buttonDisabled: boolean;
  buttonOnClick: () => void;
  className?: string;
  observeResize?: boolean;
}

const useBlockDialogEscape = (shouldBlock: boolean) => {
  useEffect(() => {
    const event = new CustomEvent('dialog-escape-block', {
      detail: { blocked: shouldBlock },
    });
    window.dispatchEvent(event);

    // Clean up on unmount or when blocking changes
    return () => {
      if (shouldBlock) {
        const unblockEvent = new CustomEvent('dialog-escape-block', {
          detail: { blocked: false },
        });
        window.dispatchEvent(unblockEvent);
      }
    };
  }, [shouldBlock]);
};

const DialogForm = ({
  showDialog,
  onOpenChange,
  title,
  description,
  isLoading = false,
  hasLoaded = true,
  children,
  buttonId,
  buttonText,
  buttonDisabled,
  buttonOnClick,
  className,
  observeResize = true,
}: DialogFormProps) => {
  const { toasts } = useToast();

  const [escapeBlocked, setEscapeBlocked] = useState(false);

  useEffect(() => {
    const handleBlockEscape = (e: CustomEvent) => {
      setEscapeBlocked(e.detail.blocked);
    };

    window.addEventListener('dialog-escape-block' as any, handleBlockEscape);
    return () => {
      window.removeEventListener(
        'dialog-escape-block' as any,
        handleBlockEscape
      );
    };
  }, []);

  return (
    <Dialog
      open={showDialog}
      onOpenChange={(open) => {
        // Only allow closing if escape is not blocked by a child component
        if (!open && escapeBlocked) {
          return; // Prevent closing
        }
        onOpenChange(open);
      }}
    >
      <DialogContent className="pt-12 pl-12 pr-8 max-w-3xl lg:h-[760px] 2xl:h-[900px] h-[500px]">
        <ToasterInDialog toasts={toasts}>
          <DialogHeader className="h-[32px]">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {isLoading && (
            <div className="'lg:h-[500px] 2xl:h-[650px] h-[250px]">
              <LoadingSpinner></LoadingSpinner>
            </div>
          )}
          {hasLoaded && (
            <ScrollArea className="pr-4" observeResize={observeResize}>
              <div
                className={cn(
                  'lg:h-[500px] 2xl:h-[650px] h-[250px] space-y-6',
                  className
                )}
              >
                {children}
              </div>
            </ScrollArea>
          )}
          <DialogFooter className="h-[64px] pr-4">
            <Button
              className="mt-4"
              id={buttonId}
              disabled={buttonDisabled}
              onClick={buttonOnClick}
              type="submit"
            >
              {buttonText}
            </Button>
          </DialogFooter>
        </ToasterInDialog>
      </DialogContent>
    </Dialog>
  );
};

export {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogForm,
  useBlockDialogEscape,
};
